<template>
  <component
    :is="rootComponent"
    class="reports-table-cell__wrapper"
    v-bind="rootProps"
    @click.stop
  >
    <div
      v-if="
        params.data?.isStaticList ||
        params.data?.isFolder ||
        params.data?.isDisabledByTier ||
        !canEditReport
      "
    >
      --
    </div>
    <div
      v-else
      :class="{ 'opacity-50 c-not-allowed': cannotEditNotifications }"
      class="flex items-center pr-40 justify-center"
    >
      <RouterLink
        :to="notificationsModalRoute"
        :class="{ 'pointer-events-none': cannotEditNotifications }"
        class="leading-none"
      >
        <BittsTooltip
          v-if="reportNotifications.length || isOwnGreenfieldReport"
          :mount-to-body="true"
          overlay-class="report-notification-cell__tooltip"
          placement="bottom"
          :class="{ 'c-not-allowed ml-12': isOwnGreenfieldReport }"
        >
          <div
            v-if="isOwnGreenfieldReport"
            class="report-notification-cell__greenfield"
          >
            <FontAwesomeIcon
              :icon="['fak', 'notifications-off']"
              :style="{ height: '12px', width: '12px', color: 'currentColor' }"
              class="text-neutral-accent"
            />
          </div>
          <BittsTag v-else color="accent" size="x-small" class="mb-4">
            <template #pre>
              <FontAwesomeIcon
                :icon="['fak', 'notifications-on']"
                :style="{
                  height: '12px',
                  width: '12px',
                  color: 'currentColor',
                }"
                class="mr-4 text-accent-accent"
              />
            </template>
            On
          </BittsTag>
          <template #title> {{ tooltipText }} </template>
        </BittsTooltip>
        <BittsTag v-else color="neutral" size="x-small">
          <template #pre>
            <FontAwesomeIcon
              :icon="['fak', 'notifications-off']"
              :style="{ height: '12px', width: '12px', color: 'currentColor' }"
              class="mr-4 text-neutral-accent"
            />
          </template>
          Off
        </BittsTag>
      </RouterLink>
    </div>
    <template #title>
      {{ cannotEditText }}
    </template>
  </component>
</template>

<script setup lang="ts">
import { BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Ref, computed } from 'vue';
import { useRoute } from 'vue-router';

import { useReportPermissions } from '@/composables/useReportPermissions';
import { OWN_GREENFIELD } from '@/constants/reports';
import { CHILD_ROUTES } from '@/constants/routes';
import { Report, ReportCell, ReportNotification } from '@/types/reports';

const { params } = defineProps<{ params: ICellRendererParams<ReportCell> }>();

const NOTIFS_TO_TOOLTIP_TEXT_MAP = {
  'slack-and-email': '🔔️ Email & Slack',
  slack: '🔔️ Slack',
  email: '🔔️ Email',
};

const reportNotifications = computed(() =>
  params.data?.isStaticList || params.data?.isFolder
    ? []
    : params.data?.report?.notification_configs || [],
);
const report = computed(() => params.data?.report);
const { canEditReport } = useReportPermissions(report as Ref<Partial<Report>>);

const hasEmailNotif = computed(
  () =>
    !!reportNotifications.value?.find(
      (notif: ReportNotification) => !!notif.emails,
    ),
);
const hasSlackNotif = computed(
  () =>
    !!reportNotifications.value?.find(
      (notif: ReportNotification) => !!notif.slack_channel,
    ),
);

const route = useRoute();
const isOnPartnerDetailsPage = computed(() => {
  return route.name === 'partner_details';
});

const isOwnGreenfieldReport = computed(
  () =>
    !params.data?.isStaticList &&
    !params.data?.isFolder &&
    params.data?.report?.consolidated_report_type === OWN_GREENFIELD,
);
const isInErrorState = computed(
  () =>
    (!params.data?.isStaticList &&
      !params.data?.isFolder &&
      params.data?.report?.status?.errors.length) ||
    false,
);
const cannotEditNotifications = computed(() => {
  return (
    isInErrorState.value || isOwnGreenfieldReport.value || !canEditReport.value
  );
});

const routeName = computed(() => {
  if (isOnPartnerDetailsPage.value) {
    return 'partner_details.report_notifications';
  } else if (params.data?.isOnReportsPage) {
    if (route.name === 'lists') {
      return 'lists.notifications';
    }
    return CHILD_ROUTES.reports.REPORTS_NOTIFICATIONS;
  }
  return CHILD_ROUTES.folder.REPORTS_NOTIFICATIONS;
});
const notificationsModalRoute = computed(() => {
  if (params.data?.isOnReportsPage) {
    // lists.notifications doesn't have :id in path, only use query param
    if (route.name === 'lists') {
      return {
        name: routeName.value,
        query: { id: params.data?.report?.id || '' },
      };
    }
    // report_notifications has :id in path
    return {
      name: routeName.value,
      params: { id: params.data?.report?.id || '' },
      query: { id: params.data?.report?.id || '' },
    };
  }
  return {
    name: routeName.value,
    params: {
      folder_id:
        params.data?.isStaticList || params.data?.isFolder
          ? null
          : params.data?.report?.folder_uuid || '',
    },
    query: { id: params.data?.report?.id || '' },
  };
});

const rootComponent = computed(() =>
  cannotEditNotifications.value ? BittsTooltip : 'div',
);
const rootProps = computed(() =>
  cannotEditNotifications.value
    ? { placement: 'bottom', mountToBody: true }
    : {},
);

const tooltipText = computed(() => {
  if (hasEmailNotif.value && hasSlackNotif.value) {
    return NOTIFS_TO_TOOLTIP_TEXT_MAP['slack-and-email'];
  } else if (hasEmailNotif.value) {
    return NOTIFS_TO_TOOLTIP_TEXT_MAP.email;
  } else if (hasSlackNotif.value) {
    return NOTIFS_TO_TOOLTIP_TEXT_MAP.slack;
  }
  return 'Notifications are not available for this List type';
});

const cannotEditText = computed(() => {
  if (!canEditReport.value) {
    return 'You do not have permission to edit notifications';
  }
  if (isInErrorState.value) {
    return 'Notifications are paused until List errors have been addressed';
  }
  return 'Notifications are not available for this List type';
});
</script>

<style lang="pcss">
@reference "../../css/theme.css";
.c-not-allowed {
  cursor: not-allowed;
}

.report-notification-cell__greenfield {
  @apply flex justify-center p-4 text-neutral-500 rounded-bts-sm bg-neutral-50;
}
</style>
